import {
    SessionContinueRequest,
    StartSessionResponse,
    DataResponse,
    SessionStates,
    StartSessionForMultiplePackagesResponse,
    PackageSetSession,
    SessionState,
    IotaAppComment,
    SessionFilter,
    ApplicationSession
} from './types';
import appClient from './AppClient';
import { endpoints } from '../../../config';

export type Filter = {
    [key: string]: unknown;
};

export default class SessionService {
    getSessionState(sessionId: string) {
        const url = endpoints.manageUrl + `iotaruntime/applications/sessions/${sessionId}/state`;
        return appClient.get<SessionState>(url);
    }

    getSessionStateFromIota(runtimeSessionId: string, extension: string) {
        const url = endpoints.iotaUrl + `runtime/${extension}/${runtimeSessionId}`;
        return appClient.get<SessionState>(url);
    }

    updateSession(sessionId: string, state: SessionStates) {
        const request = {
            id: sessionId,
            state: state
        };
        const url = endpoints.manageUrl + 'iotaruntime/applications/sessions';
        return appClient.update(url, request);
    }

    startSession(applicationId: string, packageId: string, replacesSessionId?: string) {
        const url = endpoints.manageUrl + `iotaruntime/applications/${applicationId}/sessions/${packageId}`;
        return appClient.post<StartSessionResponse>(url, { replacesSessionId });
    }

    startSessionForPackageSet(applicationId: string, packageSetId: string, isBulk: boolean = true) {
        const url =
            endpoints.manageUrl +
            `iotaruntime/applications/${applicationId}/process-set/${packageSetId}?isBulk=${isBulk}`;
        return appClient.post<string[]>(url, {});
    }

    startSessionForMultiplePackages(applicationId: string, packageIds: string[]) {
        var request = {
            packageIds
        };
        const url = endpoints.manageUrl + `iotaruntime/applications/${applicationId}/process-packages`;
        return appClient.post<StartSessionForMultiplePackagesResponse>(url, request);
    }

    continueSession(values: {}, actionId: string, runtimeSessionId: string, extension: string) {
        const requestBody: SessionContinueRequest = {
            inputs: values,
            action: actionId
        };

        const url = endpoints.iotaUrl + `runtime/${extension}/${runtimeSessionId}`;
        return appClient.post(url, requestBody);
    }

    // eslint-disable-next-line max-len
    async loadValuesForWidgets(
        sessionId: string,
        link: string,
        applicationId: string,
        extension: string,
        page: number = 0,
        pageSize: number = 9999,
        filter: Filter | undefined = undefined,
        parentValues?: string[] | null,
        parentValuesByInputId?: Record<string, string[]>,
        ignoreCache?: boolean
    ) {
        const url = endpoints.iotaUrl + `connections/${extension}/${applicationId}/${link}/${sessionId}`;
        const request = {
            page: page,
            pageSize: pageSize,
            connectionId: link,
            fields: ['text', 'value'],
            filters: filter
                ? [
                      {
                          filters: Object.getOwnPropertyNames(filter).map(f => ({
                              field: f,
                              value: filter[f],
                              operator: 'Contains'
                          })),
                          logicalOperation: 'and'
                      }
                  ]
                : undefined,
            parentInputValues: parentValues,
            parentInputValuesByInputId: parentValuesByInputId,
            ignoreCache
        };

        const requestBody = {
            request
        };

        return appClient.post<DataResponse>(url, requestBody);
    }

    async getAllSessions() {
        const url = endpoints.manageUrl + 'iotaruntime/applications/sessions';
        const result = await appClient.get<ApplicationSession[]>(url);

        if (result.isErr()) {
            return [];
        }

        return result.value || [];
    }

    async getSessionsForProjects(filter?: SessionFilter) {
        const url = endpoints.manageUrl + 'iotaruntime/applications/sessions/search-for-projects';
        const result = await appClient.post<{ sessions: ApplicationSession[]; total: number }>(url, filter || null);

        if (result.isErr()) {
            return {
                sessions: [],
                total: 0
            };
        }

        return {
            sessions: result.value.sessions,
            total: result.value.total
        };
    }

    async getArchiveUserIdsForProjects(projectIds: string[]) {
        const url = endpoints.manageUrl + 'iotaruntime/applications/sessions/archive-users/search-for-projects';
        const result = await appClient.post<string[]>(url, { projectIds });

        if (result.isErr()) {
            throw result.error;
        }

        return result.value;
    }

    async getPackageSetSession(id: string) {
        const url =
            endpoints.manageUrl + `iotaruntime/applications/package-set-sessions/${encodeURIComponent(id)}`;
        const result = await appClient.get<PackageSetSession | null>(url);

        if (result.isErr()) {
            return undefined;
        }

        return result.value || undefined;
    }

    async getFileFromIotaTempStorage(filePath: string) {
        const url = `${endpoints.iotaUrl}files/download`;
        const resp = await appClient.post(url, { filePath }, 'blob');
        resp.map(data => {
            const blobURL = URL.createObjectURL(data as Blob);
            const tempLink = document.createElement('a');
            tempLink.style.display = 'none';
            tempLink.href = blobURL;
            tempLink.setAttribute('download', filePath.replace(/^.*[\\/]/, ''));
            if (typeof tempLink.download === 'undefined') {
                tempLink.setAttribute('target', '_blank');
            }
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            setTimeout(() => {
                URL.revokeObjectURL(blobURL);
            }, 100);
        });
    }

    async getSessionById(sessionId: string, projectIds?: string[]) {
        const projectIdsQuery = projectIds?.length
            ? `?${projectIds.map(id => `projectIds=${encodeURIComponent(id)}`).join('&')}`
            : '';
        const url =
            endpoints.manageUrl + `iotaruntime/applications/sessions/${encodeURIComponent(sessionId)}${projectIdsQuery}`;
        const result = await appClient.get<ApplicationSession | null>(url);

        if (result.isOk()) {
            return result.value;
        }

        return null;
    }

    async getComments(sessionId: string) {
        const url =
            endpoints.iotaUrl +
            `appfieldcomments/applications/sessions/${encodeURIComponent(sessionId)}/field-comments`;
        const result = await appClient.get<IotaAppComment[]>(url);

        if (result.isErr()) {
            return [];
        }

        return result.value || [];
    }

    async saveFieldComment(sessionId: string, fieldId: string, comment: string) {
        const requestBody = {
            comment
        };
        const url = `${endpoints.iotaUrl}appfieldcomments/applications/sessions/${sessionId}/field-comment/${fieldId}`;
        return appClient.post(url, requestBody);
    }
}
